import styled from 'styled-components/macro';
import css from '@styled-system/css';
import { variant } from 'styled-system';

type VariantNames =
  | 'primary'
  | 'small'
  | 'transparent'
  | 'search'
  | 'clear'
  | 'clearBold';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: VariantNames;
  error?: boolean;
  success?: boolean;
  readonly?: boolean;
}

export const Input = styled.input<InputProps>(
  (props) =>
    css({
      display: 'block',
      width: '100%',
      p: '3px 20px',
      appearance: 'none',
      fontFamily: 'body',
      fontSize: 'up',
      lineHeight: 'input',
      border: '1px solid',
      borderColor: 'input.border',
      textAlign: 'center',
      borderRadius: 'small',
      boxShadow: 'none',
      opacity: '1',
      color: props?.error ? 'input.textError' : 'input.text',
      pointerEvents: props?.readonly ? 'none' : 'auto',

      '&::autofill': {
        backgroundColor: 'input.bg'
      },

      '&:hover, &:focus': {
        borderColor: props?.success ? 'primary.regular' : 'input.focus'
      },

      '&.is--error': {
        color: 'input.textError',
        borderColor: 'input.textError'
      },

      '&:placeholder': {
        color: props?.error ? 'input.textError' : 'input.placeholder',
        opacity: 1
      },

      '&:disabled': {
        opacity: 0.5,
        pointerEvents: 'none',

        '&:hover': {
          borderColor: 'input.border'
        }
      },

      '&.disabled': {
        cursor: 'not-allowed',
        opacity: 0.5
      }
    }),
  (props) =>
    variant({
      variants: {
        small: {
          p: '3px 10px',
          fontSize: 'xs',
          fontWeight: 'semi',
          borderRadius: 'little',
          lineHeight: '16px',
          borderColor: 'colors.text.border'
        },
        transparent: {
          p: '0',
          fontSize: 'xs',
          fontWeight: 'semi',
          color: 'input.text',
          textAlign: 'right',
          border: 'none',
          backgroundColor: 'transparent',

          '&:focus': {
            outline: 'none',
            border: 'none'
          }
        },
        primary: {
          p: '10px 16px',
          mb: '40px',
          backgroundColor: '#fff',
          textAlign: 'left',
          fontSize: 'xs',
          borderColor: 'text.border'
        },
        search: {
          mb: '16px',
          p: '14px 15px',
          borderColor: props?.success ? 'primary.regular' : 'text.border',
          color: '#8b8b8b',
          lineHeight: '16px',
          fontSize: 'sm',
          fontWeight: 400,
          textAlign: 'left',
          backgroundColor: '#f7f7f7',

          '&::placeholder': {
            color: '#c2c9df',
            opacity: 1
          },

          '&:focus, &:hover:not(:disabled)': {
            borderColor: props?.success ? 'primary.regular' : 'text.border'
          }
        },
        clear: {
          mb: 0,
          p: 0,
          textAlign: 'left',
          border: 'none',
          width: 'auto',
          color: 'text.border',
          fontSize: 'sm',
          fontWeight: 'regular',
          lineHeight: 'body',

          '&::placeholder': {
            color: 'text.border',
            opacity: 1
          }
        },
        clearBold: {
          mb: 0,
          p: 0,
          textAlign: 'left',
          border: 'none',
          width: 'auto',
          color: 'input.placeholder',
          fontSize: 'up',
          fontWeight: 'bold',

          '&::placeholder': {
            color: 'input.placeholder',
            opacity: 1
          }
        }
      }
    })
);
